import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  InputAdornment,
  Box,
  Typography,
  Divider,
  IconButton,
  Alert,
} from '@mui/material';
import {
  CloseOutlined,
  PersonOutlined,
  BusinessOutlined,
  EmailOutlined,
  PhoneOutlined,
  LanguageOutlined,
  LocationOnOutlined,
  AttachMoneyOutlined,
  LabelOutlined,
  SaveOutlined,
} from '@mui/icons-material';

export interface LeadFormData {
  name: string;
  email?: string;
  phone?: string;
  companyName?: string;
  position?: string;
  source: string;
  status: string;
  priority: string;
  estimatedBudget?: number;
  industry?: string;
  location?: string;
  website?: string;
  employeeCount?: string;
  tags?: string[];
  notes?: string;
}

interface LeadFormProps {
  open: boolean;
  mode: 'create' | 'edit';
  initialData?: Partial<LeadFormData>;
  onClose: () => void;
  onSubmit: (data: LeadFormData) => void | Promise<void>;
  isSubmitting?: boolean;
}

const sourceOptions = [
  { value: 'LINKEDIN', label: 'LinkedIn' },
  { value: 'WEBSITE', label: 'Website' },
  { value: 'REFERRAL', label: 'Referral 推薦' },
  { value: 'COLD_CALL', label: 'Cold Call 陌生拜訪' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'EVENT', label: 'Event 活動' },
  { value: 'OTHER', label: 'Other 其他' },
];

const statusOptions = [
  { value: 'NEW', label: 'New 新增' },
  { value: 'CONTACTED', label: 'Contacted 已聯繫' },
  { value: 'QUALIFIED', label: 'Qualified 合格' },
  { value: 'UNQUALIFIED', label: 'Unqualified 不合格' },
  { value: 'CONVERTED', label: 'Converted 已轉換' },
  { value: 'LOST', label: 'Lost 已流失' },
];

const priorityOptions = [
  { value: 'URGENT', label: 'Urgent 緊急' },
  { value: 'HIGH', label: 'High 高' },
  { value: 'MEDIUM', label: 'Medium 中' },
  { value: 'LOW', label: 'Low 低' },
];

const industryOptions = [
  'Technology 科技',
  'Finance 金融',
  'Healthcare 醫療',
  'Retail 零售',
  'Manufacturing 製造',
  'Education 教育',
  'Real Estate 房地產',
  'Consulting 諮詢',
  'Other 其他',
];

const employeeCountOptions = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1000+',
];

const commonTags = [
  'Hot Lead 熱門',
  'Enterprise 企業',
  'SMB 中小企業',
  'Decision Maker 決策者',
  'Budget Approved 預算已批准',
  'Competitor User 競品用戶',
  'Referral 推薦',
  'Event Contact 活動聯繫人',
];

const LeadForm: React.FC<LeadFormProps> = ({
  open,
  mode,
  initialData,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<LeadFormData>({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    position: '',
    source: 'WEBSITE',
    status: 'NEW',
    priority: 'MEDIUM',
    estimatedBudget: undefined,
    industry: '',
    location: '',
    website: '',
    employeeCount: '',
    tags: [],
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when dialog opens or initialData changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && initialData) {
        setFormData({
          name: initialData.name || '',
          email: initialData.email || '',
          phone: initialData.phone || '',
          companyName: initialData.companyName || '',
          position: initialData.position || '',
          source: initialData.source || 'WEBSITE',
          status: initialData.status || 'NEW',
          priority: initialData.priority || 'MEDIUM',
          estimatedBudget: initialData.estimatedBudget,
          industry: initialData.industry || '',
          location: initialData.location || '',
          website: initialData.website || '',
          employeeCount: initialData.employeeCount || '',
          tags: initialData.tags || [],
          notes: initialData.notes || '',
        });
      } else {
        // Reset form for create mode
        setFormData({
          name: '',
          email: '',
          phone: '',
          companyName: '',
          position: '',
          source: 'WEBSITE',
          status: 'NEW',
          priority: 'MEDIUM',
          estimatedBudget: undefined,
          industry: '',
          location: '',
          website: '',
          employeeCount: '',
          tags: [],
          notes: '',
        });
      }
      setErrors({});
    }
  }, [open, mode, initialData]);

  const handleChange = (field: keyof LeadFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required 名稱為必填';
    }

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format 無效的電郵格式';
    }

    // Website validation
    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = 'Website must start with http:// or https://';
    }

    // Budget validation
    if (formData.estimatedBudget && formData.estimatedBudget < 0) {
      newErrors.estimatedBudget = 'Budget cannot be negative 預算不能為負數';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      handleClose();
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      companyName: '',
      position: '',
      source: 'WEBSITE',
      status: 'NEW',
      priority: 'MEDIUM',
      estimatedBudget: undefined,
      industry: '',
      location: '',
      website: '',
      employeeCount: '',
      tags: [],
      notes: '',
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {mode === 'create' ? 'Add New Lead 新增潛在客戶' : 'Edit Lead 編輯潛在客戶'}
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={isSubmitting}>
            <CloseOutlined />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Contact Information Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Contact Information 聯繫資訊
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Contact Name 聯繫人姓名 *"
              fullWidth
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              disabled={isSubmitting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlined />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Email 電郵"
              fullWidth
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              disabled={isSubmitting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone 電話"
              fullWidth
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              disabled={isSubmitting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneOutlined />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Position 職位"
              fullWidth
              value={formData.position}
              onChange={(e) => handleChange('position', e.target.value)}
              disabled={isSubmitting}
            />
          </Grid>

          {/* Company Information Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
              Company Information 公司資訊
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Company Name 公司名稱"
              fullWidth
              value={formData.companyName}
              onChange={(e) => handleChange('companyName', e.target.value)}
              disabled={isSubmitting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessOutlined />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={industryOptions}
              value={formData.industry}
              onChange={(_, newValue) => handleChange('industry', newValue || '')}
              disabled={isSubmitting}
              freeSolo
              renderInput={(params) => (
                <TextField {...params} label="Industry 行業" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Location 地點"
              fullWidth
              value={formData.location}
              onChange={(e) => handleChange('location', e.target.value)}
              disabled={isSubmitting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnOutlined />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={isSubmitting}>
              <InputLabel>Employee Count 員工數</InputLabel>
              <Select
                value={formData.employeeCount}
                label="Employee Count 員工數"
                onChange={(e) => handleChange('employeeCount', e.target.value)}
              >
                <MenuItem value="">
                  <em>Not specified 未指定</em>
                </MenuItem>
                {employeeCountOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Website 網站"
              fullWidth
              value={formData.website}
              onChange={(e) => handleChange('website', e.target.value)}
              error={!!errors.website}
              helperText={errors.website || 'e.g., https://example.com'}
              disabled={isSubmitting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LanguageOutlined />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Estimated Budget 預估預算 (USD)"
              fullWidth
              type="number"
              value={formData.estimatedBudget || ''}
              onChange={(e) => handleChange('estimatedBudget', e.target.value ? Number(e.target.value) : undefined)}
              error={!!errors.estimatedBudget}
              helperText={errors.estimatedBudget}
              disabled={isSubmitting}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoneyOutlined />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Lead Management Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
              Lead Management 潛客管理
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth disabled={isSubmitting}>
              <InputLabel>Source 來源 *</InputLabel>
              <Select
                value={formData.source}
                label="Source 來源 *"
                onChange={(e) => handleChange('source', e.target.value)}
              >
                {sourceOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth disabled={isSubmitting}>
              <InputLabel>Status 狀態 *</InputLabel>
              <Select
                value={formData.status}
                label="Status 狀態 *"
                onChange={(e) => handleChange('status', e.target.value)}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth disabled={isSubmitting}>
              <InputLabel>Priority 優先級 *</InputLabel>
              <Select
                value={formData.priority}
                label="Priority 優先級 *"
                onChange={(e) => handleChange('priority', e.target.value)}
              >
                {priorityOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={commonTags}
              value={formData.tags}
              onChange={(_, newValue) => handleChange('tags', newValue)}
              disabled={isSubmitting}
              freeSolo
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    size="small"
                    key={index}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags 標籤"
                  placeholder="Select or type tags..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <LabelOutlined />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Notes 備註"
              fullWidth
              multiline
              rows={4}
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              disabled={isSubmitting}
              placeholder="Add any additional notes about this lead..."
            />
          </Grid>

          {Object.keys(errors).length > 0 && (
            <Grid item xs={12}>
              <Alert severity="error">
                Please fix the errors above before submitting. 請先修正上述錯誤再提交。
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel 取消
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={<SaveOutlined />}
        >
          {isSubmitting
            ? 'Saving... 儲存中...'
            : mode === 'create'
            ? 'Create Lead 建立潛客'
            : 'Save Changes 儲存變更'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadForm;
